import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { addDays, isAfter, isToday, isSameWeek, isSameMonth, addMonths } from 'date-fns';
import { propertiesAPI, guestPagesAPI } from '../services/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};

const DEMO_PROPERTY_ID = 'demo-property-001';
const MOUNTAIN_MEMORIES_ID = 'prop-mountain-memories';
const DEMO_OWNER_ID = '1';
const DEMO_GUEST_PAGE_SLUG = 'demo-guest';

const INITIAL_DEMO_PROPERTY = {
  id: DEMO_PROPERTY_ID,
  ownerId: DEMO_OWNER_ID,
  name: "Seaside Escape (Demo)",
  address: "123 Ocean View Dr, Malibu, CA",
  image: "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  wifiName: "Seaside_Guest",
  wifiPassword: "wavesandsunshine",
  checkInTime: "15:00",
  checkOutTime: "11:00",
  houseRules: "1. No smoking inside.\n2. Quiet hours after 10 PM.\n3. Please rinse sand off before entering.",
  checkoutProcedures: "1. Load dishwasher.\n2. Lock all doors.\n3. Leave keys in the lockbox.",
  tvInstructions: "Use the Samsung remote. Press 'Home' to access Netflix and Hulu.",
  fireplaceInstructions: "Turn the dial to the right to ignite. Do not leave unattended.",
  supportContact: "Host: (555) 019-2834",
  localRecommendations: "Best Coffee: Morning Brew Co.\nBest Dinner: The Catch Seafood.",
  createdAt: new Date().toISOString()
};

const MOUNTAIN_MEMORIES_PROPERTY = {
  id: MOUNTAIN_MEMORIES_ID,
  ownerId: DEMO_OWNER_ID,
  name: "Mountain Memories",
  address: "78 Mocha Ct, Ellijay, GA 30540",
  image: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  wifiName: "MountainMemories_Guest",
  wifiPassword: "relaxinthemountains",
  checkInTime: "16:00",
  checkOutTime: "10:00",
  houseRules: "1. No shoes on carpet.\n2. Please cover hot tub when not in use.\n3. Bear safety: Lock all trash in the bin.",
  checkoutProcedures: "1. Start a load of towels.\n2. Set thermostat to 72.\n3. Lock all windows and doors.",
  tvInstructions: "Roku remotes are in each room.",
  fireplaceInstructions: "Gas fireplace: Flip the switch on the wall to the right.",
  supportContact: "Host: (555) 987-6543",
  localRecommendations: "Hiking: Amicalola Falls.\nDining: The Roof in Ellijay.",
  createdAt: new Date().toISOString()
};

const INITIAL_DEMO_GUEST_PAGE = {
  id: 'demo-page-001',
  propertyId: DEMO_PROPERTY_ID,
  guestName: "Alex Renter",
  guestEmail: "alex@example.com",
  phone: "(555) 123-1234",
  lastFourDigits: "1234",
  checkInDate: new Date().toISOString().split('T')[0],
  checkOutDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
  doorCode: "9876",
  reservationId: "RES-123456",
  specialInstructions: "Welcome! We left a welcome basket in the kitchen.",
  status: 'active',
  slug: DEMO_GUEST_PAGE_SLUG,
  createdAt: new Date().toISOString()
};

const INITIAL_DEMO_APPLIANCE = {
  id: 'demo-app-001',
  propertyId: DEMO_PROPERTY_ID,
  type: "Smart TV",
  location: "Living Room",
  brand: "Samsung",
  model: "QLED 4K",
  instructions: "Press the Home button to access streaming apps. Guest mode is enabled.",
  createdAt: new Date().toISOString()
};

const INITIAL_EMAIL_TEMPLATES = {
  welcome: {
    subject: "Your Upcoming Stay at {{propertyName}}",
    body: "Hi {{guestName}},\n\nWe're looking forward to your stay! Here is the link to your private guest portal with all the information you'll need:\n\n{{portalLink}}\n\nYour access code is the last 4 digits of your phone number.\n\nBest,\nThe {{propertyName}} Team"
  },
  checkin_reminder: {
    subject: "Check-in Reminder for {{propertyName}}",
    body: "Hi {{guestName}},\n\nJust a friendly reminder that your check-in is tomorrow, {{checkInDate}}.\n\nYour portal link is: {{portalLink}}\n\nWe can't wait to host you!"
  },
  checkout_reminder: {
    subject: "Checkout Information for {{propertyName}}",
    body: "Hi {{guestName}},\n\nWe hope you enjoyed your stay! As a reminder, check-out is tomorrow at {{checkOutTime}}.\n\nPlease follow the checkout procedures outlined in your guest portal:\n{{portalLink}}\n\nSafe travels!"
  }
};

const INITIAL_EMAIL_SEQUENCES = [
  { id: uuidv4(), template: 'welcome', trigger: 'after_booking', days: 0, time: '10:00', enabled: true },
  { id: uuidv4(), template: 'checkin_reminder', trigger: 'before_checkin', days: 1, time: '17:00', enabled: true },
  { id: uuidv4(), template: 'checkout_reminder', trigger: 'before_checkout', days: 1, time: '18:00', enabled: true }
];

const INITIAL_SMTP_SETTINGS = {
  host: '',
  port: '587',
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  encryption: 'tls',
  enabled: false
};

const INITIAL_CRON_SETTINGS = {
  enabled: false,
  timezone: 'UTC',
  frequency: 'hourly',
  customCron: '',
  lastRun: null,
  nextRun: null,
  retryAttempts: 3,
  retryDelay: 5
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [allProperties, setAllProperties] = useState([]);
  const [allGuestPages, setAllGuestPages] = useState([]);
  const [allAppliances, setAllAppliances] = useState([]);
  const [allFaqs, setAllFaqs] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [automationSettings, setAutomationSettings] = useState({
    emailParsing: false,
    webhookUrl: '',
    zapierEnabled: false
  });
  const [emailTemplates, setEmailTemplates] = useState(INITIAL_EMAIL_TEMPLATES);
  const [emailSequences, setEmailSequences] = useState(INITIAL_EMAIL_SEQUENCES);
  const [smtpSettings, setSmtpSettings] = useState(INITIAL_SMTP_SETTINGS);
  const [cronSettings, setCronSettings] = useState(INITIAL_CRON_SETTINGS);
  
  // New Global Filter State
  const [dateFilter, setDateFilter] = useState('This Month');

  const loadFromStorage = (key, setter, defaultValue, seedData) => {
    const stored = localStorage.getItem(key);
    let data = stored ? JSON.parse(stored) : (defaultValue || []);
    if (seedData && data.length === 0) {
      data.push(...seedData);
    }
    setter(data);
  };

  useEffect(() => {
    // Load from localStorage initially (for demo/offline mode)
    let loadedProperties = JSON.parse(localStorage.getItem('rentalPortal_allProperties')) || [];
    let loadedGuestPages = JSON.parse(localStorage.getItem('rentalPortal_allGuestPages')) || [];
    
    setAllProperties(loadedProperties);
    setAllGuestPages(loadedGuestPages);
    loadFromStorage('rentalPortal_allAppliances', setAllAppliances, [], [INITIAL_DEMO_APPLIANCE]);
    loadFromStorage('rentalPortal_allFaqs', setAllFaqs, []);
    setIsInitialized(true);
  }, []);

  // Load data from API when user logs in
  useEffect(() => {
    if (user) {
      const loadDataFromAPI = async () => {
        try {
          const [properties, guestPages] = await Promise.all([
            propertiesAPI.getAll().catch(() => []),
            guestPagesAPI.getAll().catch(() => [])
          ]);
          
          // Transform API response to match frontend format
          const transformedProperties = properties.map(p => ({
            id: p.id,
            ownerId: p.owner_id,
            name: p.name,
            address: p.address,
            image: p.image,
            backgroundImage: p.background_image,
            wifiName: p.wifi_name,
            wifiPassword: p.wifi_password,
            checkInTime: p.check_in_time,
            checkOutTime: p.check_out_time,
            houseRules: p.house_rules,
            checkoutProcedures: p.checkout_procedures,
            tvInstructions: p.tv_instructions,
            fireplaceInstructions: p.fireplace_instructions,
            supportContact: p.support_contact,
            localRecommendations: p.local_recommendations,
            additionalInstructions: p.additional_instructions ? (typeof p.additional_instructions === 'string' ? JSON.parse(p.additional_instructions) : p.additional_instructions) : [],
            createdAt: p.created_at
          }));
          
          const transformedGuestPages = guestPages.map(gp => ({
            id: gp.id,
            propertyId: gp.property_id,
            guestName: gp.guest_name,
            guestEmail: gp.guest_email,
            phone: gp.phone,
            lastFourDigits: gp.last_four_digits,
            checkInDate: gp.check_in_date,
            checkOutDate: gp.check_out_date,
            doorCode: gp.door_code,
            reservationId: gp.reservation_id,
            specialInstructions: gp.special_instructions,
            status: gp.status,
            slug: gp.slug,
            createdAt: gp.created_at
          }));
          
          setAllProperties(transformedProperties);
          setAllGuestPages(transformedGuestPages);
        } catch (error) {
          console.error('Error loading data from API:', error);
        }
      };
      
      loadDataFromAPI();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      // Load user-specific settings
      const storedAutomation = localStorage.getItem(`automation_${user.id}`);
      if (storedAutomation) setAutomationSettings(JSON.parse(storedAutomation));

      const storedTemplates = localStorage.getItem(`emailTemplates_${user.id}`);
      if (storedTemplates) setEmailTemplates(JSON.parse(storedTemplates));

      const storedSequences = localStorage.getItem(`emailSequences_${user.id}`);
      if (storedSequences) setEmailSequences(JSON.parse(storedSequences));

      const storedSmtp = localStorage.getItem(`smtpSettings_${user.id}`);
      if (storedSmtp) setSmtpSettings(JSON.parse(storedSmtp));

      const storedCron = localStorage.getItem(`cronSettings_${user.id}`);
      if (storedCron) setCronSettings(JSON.parse(storedCron));
    }
  }, [user]);

  // Save data to localStorage
  useEffect(() => {
    if (isInitialized) localStorage.setItem('rentalPortal_allProperties', JSON.stringify(allProperties));
  }, [allProperties, isInitialized]);

  useEffect(() => {
    if (isInitialized) localStorage.setItem('rentalPortal_allGuestPages', JSON.stringify(allGuestPages));
  }, [allGuestPages, isInitialized]);

  useEffect(() => {
    if (user) localStorage.setItem(`automation_${user.id}`, JSON.stringify(automationSettings));
  }, [automationSettings, user]);

  useEffect(() => {
    if (user) localStorage.setItem(`emailTemplates_${user.id}`, JSON.stringify(emailTemplates));
  }, [emailTemplates, user]);

  useEffect(() => {
    if (user) localStorage.setItem(`emailSequences_${user.id}`, JSON.stringify(emailSequences));
  }, [emailSequences, user]);

  useEffect(() => {
    if (user) localStorage.setItem(`smtpSettings_${user.id}`, JSON.stringify(smtpSettings));
  }, [smtpSettings, user]);

  useEffect(() => {
    if (user) localStorage.setItem(`cronSettings_${user.id}`, JSON.stringify(cronSettings));
  }, [cronSettings, user]);

  // Auto-archive old guest pages
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setAllGuestPages(prev => prev.map(page => 
        (page.status === 'active' && page.checkOutDate && isAfter(now, addDays(new Date(page.checkOutDate), 5)))
          ? { ...page, status: 'archived' }
          : page
      ));
    }, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Simulate cron job execution
  useEffect(() => {
    if (!cronSettings.enabled) return;

    const interval = setInterval(() => {
      const now = new Date();
      setCronSettings(prev => ({
        ...prev,
        lastRun: now.toISOString(),
        nextRun: getNextRunTime(prev.frequency)
      }));
    }, 60000); // Simulate running every minute for demo

    return () => clearInterval(interval);
  }, [cronSettings.enabled, cronSettings.frequency]);

  const getNextRunTime = (frequency) => {
    const now = new Date();
    if (frequency === 'hourly') {
      const nextRun = new Date(now.getTime() + 60 * 60 * 1000);
      return nextRun.toISOString();
    }
    if (frequency === 'daily') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow.toISOString();
    }
    return null;
  };

  // Helper function for date filtering
  const isDateInFilter = (dateString, filter) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();

    switch (filter) {
      case 'Today':
        return isToday(date);
      case 'This Week':
        return isSameWeek(date, now, { weekStartsOn: 0 });
      case 'This Month':
        return isSameMonth(date, now);
      case 'Next Month':
        return isSameMonth(date, addMonths(now, 1));
      case 'All Time':
        return true;
      default:
        return true; // Default to showing if filter is unknown
    }
  };

  const properties = user ? allProperties.filter(p => p.ownerId === user.id) : [];
  const guestPages = user ? allGuestPages.filter(gp => properties.some(p => p.id === gp.propertyId)) : [];
  const appliances = user ? allAppliances.filter(a => properties.some(p => p.id === a.propertyId)) : [];
  const faqs = user ? allFaqs.filter(f => properties.some(p => p.id === f.propertyId)) : [];

  const createProperty = async (data) => {
    if (!user) return;
    try {
      const propertyData = {
        id: uuidv4(),
        name: data.name,
        address: data.address,
        image: data.image,
        backgroundImage: data.backgroundImage,
        wifiName: data.wifiName,
        wifiPassword: data.wifiPassword,
        checkInTime: data.checkInTime,
        checkOutTime: data.checkOutTime,
        houseRules: data.houseRules,
        checkoutProcedures: data.checkoutProcedures,
        tvInstructions: data.tvInstructions,
        fireplaceInstructions: data.fireplaceInstructions,
        supportContact: data.supportContact,
        localRecommendations: data.localRecommendations,
        additionalInstructions: data.additionalInstructions
      };
      
      const saved = await propertiesAPI.create(propertyData);
      
      // Transform API response to match frontend format
      const newProp = {
        id: saved.id,
        ownerId: saved.owner_id,
        name: saved.name,
        address: saved.address,
        image: saved.image,
        backgroundImage: saved.background_image,
        wifiName: saved.wifi_name,
        wifiPassword: saved.wifi_password,
        checkInTime: saved.check_in_time,
        checkOutTime: saved.check_out_time,
        houseRules: saved.house_rules,
        checkoutProcedures: saved.checkout_procedures,
        tvInstructions: saved.tv_instructions,
        fireplaceInstructions: saved.fireplace_instructions,
        supportContact: saved.support_contact,
        localRecommendations: saved.local_recommendations,
        additionalInstructions: saved.additional_instructions ? (typeof saved.additional_instructions === 'string' ? JSON.parse(saved.additional_instructions) : saved.additional_instructions) : [],
        createdAt: saved.created_at
      };
      
      setAllProperties(p => [...p, newProp]);
      return newProp;
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  };

  const updateProperty = async (id, updates) => {
    try {
      const updateData = {
        name: updates.name,
        address: updates.address,
        image: updates.image,
        backgroundImage: updates.backgroundImage,
        wifiName: updates.wifiName,
        wifiPassword: updates.wifiPassword,
        checkInTime: updates.checkInTime,
        checkOutTime: updates.checkOutTime,
        houseRules: updates.houseRules,
        checkoutProcedures: updates.checkoutProcedures,
        tvInstructions: updates.tvInstructions,
        fireplaceInstructions: updates.fireplaceInstructions,
        supportContact: updates.supportContact,
        localRecommendations: updates.localRecommendations,
        additionalInstructions: updates.additionalInstructions
      };
      
      const saved = await propertiesAPI.update(id, updateData);
      
      // Transform API response
      const updated = {
        id: saved.id,
        ownerId: saved.owner_id,
        name: saved.name,
        address: saved.address,
        image: saved.image,
        backgroundImage: saved.background_image,
        wifiName: saved.wifi_name,
        wifiPassword: saved.wifi_password,
        checkInTime: saved.check_in_time,
        checkOutTime: saved.check_out_time,
        houseRules: saved.house_rules,
        checkoutProcedures: saved.checkout_procedures,
        tvInstructions: saved.tv_instructions,
        fireplaceInstructions: saved.fireplace_instructions,
        supportContact: saved.support_contact,
        localRecommendations: saved.local_recommendations,
        additionalInstructions: saved.additional_instructions ? (typeof saved.additional_instructions === 'string' ? JSON.parse(saved.additional_instructions) : saved.additional_instructions) : [],
        createdAt: saved.created_at
      };
      
      setAllProperties(p => p.map(prop => prop.id === id ? updated : prop));
      return updated;
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  };

  const deleteProperty = async (id) => {
    try {
      await propertiesAPI.delete(id);
      setAllProperties(p => p.filter(prop => prop.id !== id));
      setAllGuestPages(gp => gp.filter(page => page.propertyId !== id));
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  };

  const getPropertyById = (id) => allProperties.find(p => p.id === id);

  const createGuestPage = async (data) => {
    try {
      const phone = data.phone || '';
      const pageData = {
        id: uuidv4(),
        propertyId: data.propertyId,
        guestName: data.guestName,
        guestEmail: data.guestEmail,
        phone: data.phone,
        lastFourDigits: phone.replace(/\D/g, '').slice(-4),
        checkInDate: data.checkInDate,
        checkOutDate: data.checkOutDate,
        doorCode: data.doorCode,
        reservationId: data.reservationId,
        specialInstructions: data.specialInstructions,
        status: data.status || 'active',
        slug: uuidv4().replace(/-/g, '').substring(0, 12)
      };
      
      const saved = await guestPagesAPI.create(pageData);
      
      // Transform API response
      const newPage = {
        id: saved.id,
        propertyId: saved.property_id,
        guestName: saved.guest_name,
        guestEmail: saved.guest_email,
        phone: saved.phone,
        lastFourDigits: saved.last_four_digits,
        checkInDate: saved.check_in_date,
        checkOutDate: saved.check_out_date,
        doorCode: saved.door_code,
        reservationId: saved.reservation_id,
        specialInstructions: saved.special_instructions,
        status: saved.status,
        slug: saved.slug,
        createdAt: saved.created_at
      };
      
      setAllGuestPages(gp => [...gp, newPage]);
      return newPage;
    } catch (error) {
      console.error('Error creating guest page:', error);
      throw error;
    }
  };

  const updateGuestPage = async (id, updates) => {
    try {
      const updateData = {
        guestName: updates.guestName,
        guestEmail: updates.guestEmail,
        phone: updates.phone,
        lastFourDigits: updates.lastFourDigits,
        checkInDate: updates.checkInDate,
        checkOutDate: updates.checkOutDate,
        doorCode: updates.doorCode,
        reservationId: updates.reservationId,
        specialInstructions: updates.specialInstructions,
        status: updates.status
      };
      
      const saved = await guestPagesAPI.update(id, updateData);
      
      // Transform API response
      const updated = {
        id: saved.id,
        propertyId: saved.property_id,
        guestName: saved.guest_name,
        guestEmail: saved.guest_email,
        phone: saved.phone,
        lastFourDigits: saved.last_four_digits,
        checkInDate: saved.check_in_date,
        checkOutDate: saved.check_out_date,
        doorCode: saved.door_code,
        reservationId: saved.reservation_id,
        specialInstructions: saved.special_instructions,
        status: saved.status,
        slug: saved.slug,
        createdAt: saved.created_at
      };
      
      setAllGuestPages(gp => gp.map(page => page.id === id ? updated : page));
      return updated;
    } catch (error) {
      console.error('Error updating guest page:', error);
      throw error;
    }
  };

  const deleteGuestPage = async (id) => {
    try {
      await guestPagesAPI.delete(id);
      setAllGuestPages(gp => gp.filter(page => page.id !== id));
    } catch (error) {
      console.error('Error deleting guest page:', error);
      throw error;
    }
  };

  const getGuestPageBySlug = (slug) => 
    allGuestPages.find(page => page.slug === slug);

  const value = {
    isInitialized,
    properties,
    guestPages,
    appliances,
    faqs,
    allAppliances,
    allFaqs,
    automationSettings,
    emailTemplates,
    emailSequences,
    smtpSettings,
    cronSettings,
    dateFilter,
    setDateFilter,
    isDateInFilter, // Exporting the filter function
    createProperty,
    updateProperty,
    deleteProperty,
    getPropertyById,
    createGuestPage,
    updateGuestPage,
    deleteGuestPage,
    getGuestPageBySlug,
    setAutomationSettings,
    setEmailTemplates,
    setEmailSequences,
    setSmtpSettings,
    setCronSettings
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};